<script setup>
import { computed, ref, watch } from "vue";
import { useStore } from "@/core/services/store";
import { useFeatureReporting } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatureReporting";
import { useFeatures } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatures";
import { useFeatureArea } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useFeatureArea";
import ExportJournal from "@/components/ExternalApps/SalesChannelManagementApp/components/Journal/ExportJournal.vue";
import FeatureReporting from "@/components/ExternalApps/SalesChannelManagementApp/components/Journal/FeatureReporting.vue";
import Card from "@/components/Tools/Card/Card.vue";
import Progressbar from "@/components/Tools/Progressbar.vue";
import { useAppStatus } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useAppStatus";
import SkeletonLoading from "@/components/ExternalApps/SalesChannelManagementApp/components/Forms/SkeletonLoading.vue";
import CategoryReporting from "@/components/ExternalApps/SalesChannelManagementApp/components/Journal/CategoryReporting.vue";
import i18n from "@/core/plugins/vue-i18n";
import { useHelp } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useHelp";
import HelpArea from "@/components/ExternalApps/SalesChannelManagementApp/components/HelpArea.vue";
import { useIntegration } from "@/components/ExternalApps/SalesChannelManagementApp/composables/useIntegration";
import ChatBotButton from "@/components/ChatBot/components/ChatBotButton.vue";

const store = useStore();
const { isAppInitialized } = useAppStatus();
const { isReportingEnabled } = useFeatureReporting();
const { features } = useFeatures();
const { selectFeature } = useFeatureArea();
const { getLinkForTypeAndArea } = useHelp();
const { selectedIntegrationLabel } = useIntegration();

// The export journal reads the app's CFL data set. An app without one (no product export, e.g.
// avocadostore) has nothing to show there, so the tab is offered only when the id is configured.
const hasExportJournal = computed(
  () => !!store.getters.environmentVariables?.CFLDataSetId
);

const journals = computed(() => {
  const temp = hasExportJournal.value ? ["journal"] : [];

  if (features.value.length > 0) {
    for (const name of features.value) {
      if (isReportingEnabled(name)) {
        temp.push(name);
      }
    }
  }

  return temp;
});

const selectedJournal = ref(journals.value[0] ?? "journal");

// The feature tabs arrive once the integration is loaded; keep the selection on an offered tab.
watch(journals, items => {
  if (!items.includes(selectedJournal.value)) {
    selectedJournal.value = items[0] ?? "journal";
  }
});

const helpItems = computed(() => [
  {
    href: getLinkForTypeAndArea("handbook", "journal"),
    icon: "fa-book",
    label: i18n.t("salesChannelManagementApp.helpArea.handbook")
  },
  {
    href: getLinkForTypeAndArea("video", "journal"),
    icon: "fa-play",
    label: i18n.t("salesChannelManagementApp.helpArea.video")
  }
]);

function selectJournal(item) {
  selectedJournal.value = item;
  selectFeature(item);
}
</script>

<template>
  <div>
    <Progressbar />
    <div class="pt-4">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div class="d-flex">
          <!--   START: BACK BUTTON   -->
          <button
            v-b-popover.hover="$t('salesChannelManagementApp.backToOverview')"
            class=""
            @click="$router.push({ name: 'salesChannelManagementApp' })"
          >
            <i class="fal fa-chevron-left" />
          </button>
          <!--   END: BACK BUTTON   -->
          <div v-if="isAppInitialized" class="h5 mb-0 ml-5 font-weight-bolder">
            {{ selectedIntegrationLabel }}
          </div>
        </div>

        <div class="d-flex">
          <HelpArea :items="helpItems"></HelpArea>
          <ChatBotButton />
        </div>
      </div>

      <Card class="p-6">
        <div v-if="isAppInitialized">
          <div class="tabs d-flex justify-content-between mb-4">
            <PerfectScrollbar class="d-flex align-end">
              <div
                v-for="(item, i) in journals"
                :key="i"
                :class="{
                  active: selectedJournal === item
                }"
                class="tab-title py-2 px-4"
                @click="selectJournal(item)"
              >
                <span class="">{{
                  $t("salesChannelManagementApp.titles." + item)
                }}</span>
              </div>
            </PerfectScrollbar>
          </div>
          <div>
            <keep-alive>
              <ExportJournal v-if="selectedJournal === 'journal'" />

              <FeatureReporting
                v-else-if="
                  selectedJournal !== 'category' &&
                  selectedJournal !== 'journal'
                "
                :key="selectedJournal"
              />
              <CategoryReporting v-else-if="selectedJournal === 'category'" />
            </keep-alive>
          </div>
        </div>
        <div v-else>
          <SkeletonLoading />
        </div>
      </Card>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tabs {
  border-bottom: 1px solid var(--ax-border-primary, #d0d5dd);

  .tab-title {
    color: var(--ax-fg-primary, #{$color-connect-text});
    font-weight: bold;
    border: none;
    cursor: pointer;
    white-space: nowrap;

    &.active,
    &:hover {
      box-shadow: 0 -2px 0 var(--ax-bg-brand-primary, #{$color-connect-primary}) inset;
      color: var(--ax-fg-brand-primary, #{$color-connect-primary});
      text-decoration: none;
      font-weight: bold;
    }

    &.active {
      &:hover {
        color: var(--ax-fg-brand-primary, #{$color-connect-primary});
      }
    }

    &:hover {
      color: var(--ax-fg-primary, #{$color-connect-text});
    }
  }
}
</style>
